import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack
} from '@mui/material';
import PaidIcon from '@mui/icons-material/Paid';
import CancelIcon from '@mui/icons-material/Cancel';
import instance from '../../utils/axiosConfig';

function PendingRefunds() {
  const [distributors, setDistributors] = useState([]);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [refundDialog, setRefundDialog] = useState({
    open: false,
    distributor: null
  });
  const [cancelDialog, setCancelDialog] = useState({
    open: false,
    distributor: null
  });

  useEffect(() => {
    fetchPendingRefunds();
  }, []);

  const fetchPendingRefunds = async () => {
    try {
      const response = await instance.get('/api/moderator-management/awaiting-refunds');
      setDistributors(response.data);
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Error al cargar reembolsos pendientes'
      });
    }
  };

  const handleRefundClick = (distributor) => {
    setRefundDialog({
      open: true,
      distributor: distributor
    });
  };

  const handleRefundCancel = () => {
    setRefundDialog({
      open: false,
      distributor: null
    });
  };

  const handleRefundConfirm = async () => {
    try {
      await instance.post(`/api/moderator-management/confirm-refund/${refundDialog.distributor._id}`);
      setMessage({ type: 'success', text: 'Reembolso confirmado exitosamente' });
      fetchPendingRefunds();
      handleRefundCancel();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Error al confirmar reembolso'
      });
    }
  };

  const handleCancelClick = (distributor) => {
    setCancelDialog({
      open: true,
      distributor: distributor
    });
  };

  const handleCancelDialogClose = () => {
    setCancelDialog({
      open: false,
      distributor: null
    });
  };

  const handleCancelConfirm = async () => {
    try {
      await instance.post(`/api/moderator-management/cancel-refund/${cancelDialog.distributor._id}`);
      setMessage({ type: 'success', text: 'Reembolso cancelado exitosamente' });
      fetchPendingRefunds();
      handleCancelDialogClose();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Error al cancelar reembolso'
      });
    }
  };

  return (
    <Box>
      {message.text && (
        <Alert severity={message.type} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Información del Distribuidor</TableCell>
              <TableCell>Fecha de Registro</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {distributors.map((distributor) => (
              <TableRow key={distributor._id}>
                <TableCell>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {distributor.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Razón Social: {distributor.governmentName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      CUIT: {distributor.CUIT}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Email: {distributor.email}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Teléfono: {distributor.phone}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Dirección: {distributor.address}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  {new Date(distributor.createdAt).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'error.main',
                      fontWeight: 500 
                    }}
                  >
                    Pendiente de Reembolso
                  </Typography>
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1}>
                    <IconButton
                      onClick={() => handleRefundClick(distributor)}
                      color="primary"
                      title="Confirmar reembolso enviado"
                    >
                      <PaidIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleCancelClick(distributor)}
                      color="error"
                      title="Cancelar reembolso"
                    >
                      <CancelIcon />
                    </IconButton>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {distributors.length === 0 && (
        <Typography sx={{ mt: 2, textAlign: 'center' }}>
          No hay distribuidores pendientes de reembolso
        </Typography>
      )}

      <Dialog
        open={refundDialog.open}
        onClose={handleRefundCancel}
      >
        <DialogTitle>Confirmar Reembolso Enviado</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            ¿Confirmas que has enviado el reembolso al siguiente distribuidor?
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Nombre:</strong> {refundDialog.distributor?.name}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Razón Social:</strong> {refundDialog.distributor?.governmentName}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>CUIT:</strong> {refundDialog.distributor?.CUIT}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Email:</strong> {refundDialog.distributor?.email}
            </Typography>
            <Typography variant="body2">
              <strong>Teléfono:</strong> {refundDialog.distributor?.phone}
            </Typography>
          </Box>
          <Typography variant="body2" color="warning.main">
            Esta acción marcará al distribuidor como reembolsado.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRefundCancel}>
            Cancelar
          </Button>
          <Button onClick={handleRefundConfirm} color="primary" variant="contained">
            Confirmar Reembolso
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={cancelDialog.open}
        onClose={handleCancelDialogClose}
      >
        <DialogTitle>Cancelar Reembolso</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            ¿Estás seguro que deseas cancelar el reembolso del siguiente distribuidor?
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Nombre:</strong> {cancelDialog.distributor?.name}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Razón Social:</strong> {cancelDialog.distributor?.governmentName}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>CUIT:</strong> {cancelDialog.distributor?.CUIT}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Email:</strong> {cancelDialog.distributor?.email}
            </Typography>
            <Typography variant="body2">
              <strong>Teléfono:</strong> {cancelDialog.distributor?.phone}
            </Typography>
          </Box>
          <Typography variant="body2" color="error" sx={{ mb: 1 }}>
            Esta acción:
          </Typography>
          <Box component="ul" sx={{ mt: 0, color: 'error.main' }}>
            <Typography component="li" variant="body2">
              Devolverá al distribuidor al estado "Sin Verificar"
            </Typography>
            <Typography component="li" variant="body2">
              Eliminará la marca de reembolso pendiente
            </Typography>
            <Typography component="li" variant="body2">
              Permitirá que el distribuidor intente verificarse nuevamente
            </Typography>
          </Box>
          <Typography variant="body2" color="warning.main" sx={{ mt: 2 }}>
            Solo use esta opción si el reembolso no es necesario o si hubo un error en el rechazo.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDialogClose}>
            Volver
          </Button>
          <Button onClick={handleCancelConfirm} color="error" variant="contained">
            Cancelar Reembolso
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PendingRefunds; 